import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Pagination, Table, TableColumn } from '@/components/Elements';

import { GlossaryRow, PAGE_SIZE_OPTIONS } from '../constants';
import { getGlossaryLanguageI18nKey } from '../constants/glossaryLanguages';

import styles from './GlossaryList.module.scss';

/**
 * テーブル行の一意キーを生成する。
 * language:content の複合キー。
 */
const createRowKey = (entry: GlossaryRow): string =>
  `${entry.language}:${entry.content}`;

/**
 * プロパティ
 */
type Props = {
  /** 表示中の言語コード一覧（タブに使用） */
  languages: string[];
  /** 選択中の言語コード */
  currentLang: string | null;
  /** 現在ページに表示するエントリ */
  pageEntries: GlossaryRow[];
  /** 現在のページ番号（1始まり） */
  page: number;
  /** ページあたりの表示件数 */
  pageSize: number;
  /** 総ページ数 */
  totalPages: number;
  /** フィルタ後の総件数（ページ範囲表示用） */
  filteredCount: number;
  /** 言語タブ切替ハンドラー */
  onTabChange: (lang: string) => void;
  /** ページサイズ変更ハンドラー */
  onPageSizeChange: (size: number) => void;
  /** ページ移動ハンドラー */
  onPageChange: (page: number) => void;
};

/**
 * 言語タブ・用語集テーブル・ページネーションを表示する
 * 一覧コンポーネント。
 */
export const GlossaryList = React.memo(
  ({
    languages,
    currentLang,
    pageEntries,
    page,
    pageSize,
    totalPages,
    filteredCount,
    onTabChange,
    onPageSizeChange,
    onPageChange,
  }: Props) => {
    const { t } = useTranslation();

    /**
     * タブボタンクリック時に言語コードを取り出して `onTabChange` へ渡す。
     * `data-lang` 属性を経由することで `.map()` 内のインライン関数を排除する。
     */
    const handleTabClick = useCallback(
      ({ currentTarget }: React.MouseEvent<HTMLButtonElement>) => {
        const { lang } = currentTarget.dataset;

        if (lang) onTabChange(lang);
      },
      [onTabChange],
    );

    /**
     * 用語集テーブルの列定義。
     * `t` の参照が変わる（言語切替）まで再生成しない。
     */
    const columns = useMemo<TableColumn<GlossaryRow>[]>(
      () => [
        {
          key: 'content',
          header: t('glossary.columnPhrase'),
          render: (entry) => entry.content,
          width: '40%',
        },
        {
          key: 'soundsLike',
          header: t('glossary.columnSoundsLike'),
          render: (entry) => entry.soundsLike.join(', '),
          width: '60%',
        },
      ],
      [t],
    );

    return (
      <>
        <div role="tablist" className={styles.tabs}>
          {languages.map((lang) => (
            <button
              key={lang}
              type="button"
              role="tab"
              aria-selected={lang === currentLang}
              className={`${styles.tab} ${
                lang === currentLang ? styles['tab--active'] : ''
              }`}
              data-lang={lang}
              onClick={handleTabClick}
            >
              {t(
                `language.${getGlossaryLanguageI18nKey(lang)}` as Parameters<
                  typeof t
                >[0],
                { defaultValue: lang },
              )}
            </button>
          ))}
        </div>

        <Table columns={columns} rows={pageEntries} getRowKey={createRowKey} />

        <Pagination
          page={page}
          totalPages={totalPages}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          totalCount={filteredCount}
          pageSizeLabel={t('glossary.pageSizeLabel')}
          totalCountUnit={t('glossary.countUnit')}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      </>
    );
  },
);
